import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import * as Rx from 'rxjs';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { Collections, SearchCollections } from '../collections.model';
import { CollectionActions, RequestDataPayload } from './actions';
import { BackendPushResponse, StoreBackend } from './backend';
import {
  arrayIsShallowEqual,
  isDefined,
  objectFilterByKeys,
  sameElements,
  setDiff,
  toNumber,
  toObservable,
} from './helpers';
import { ListObservable } from './list-observable';
import { DatastoreMissingModuleError } from './missing-module-error';
import { ObjectObservable } from './object-observable';
import { duplicateFirst } from './operators/duplicateFirst';
import { select } from './operators/select';
import { Query } from './query';
import { RequestStatusHandler } from './request-status-handler.service';
import { SearchManager } from './search';
import { flattenQuery, stringifyQuery } from './store.helpers';
import { Path, Reference, SearchQuery, StoreState } from './store.model';
import { WebSocketService } from './websocket';

@Injectable()
export class Datastore {
  private readonly REFETCH_TIMEOUT = 10_000;

  constructor(
    private store$: Store<StoreState>,
    private storeBackend: StoreBackend,
    private webSocketService: WebSocketService,
    private searchManager: SearchManager,
    private requestStatusHandler: RequestStatusHandler,
  ) {}

  /**
   * Returns a list of objects from a collection specified by query.
   *
   * @param key1 Collection name/key
   * @param key2$ Auth user id (currently logged-in user)
   * @param queryFn Query to refine results
   */
  list<K extends keyof Collections>(
    key1: K,
    key2$: string | Rx.Observable<string>,
    queryFn?: (q: Query<K>) => Query<K> | Rx.Observable<Query<K>>,
  ): ListObservable<K> {
    const rawQuery$ = flattenQuery(queryFn);

    const requestStream$: Rx.Observable<
      RequestDataPayload<K>
    > = Rx.combineLatest(
      toObservable(key1),
      toObservable(key2$),
      rawQuery$,
    ).pipe(
      map(([collection, authUid, { limit, params, order }]) => {
        const ref: Reference<K> = {
          path: { collection, authUid },
          query: { limit, params },
          order,
        };
        const action: CollectionActions<K> = {
          type: 'REQUEST_DATA',
          payload: {
            type: ref.path.collection,
            ref,
            clientRequestIds: [this.generateClientRequestId()],
          },
        };
        this.store$.dispatch(action);
        return action.payload;
      }),
      shareReplay(1),
    );

    const sourceStream$ = requestStream$.pipe(
      tap(request =>
        this.requestStatusHandler.update(request, {
          ready: false,
        }),
      ),
      switchMap(request => {
        const {
          ref: {
            path: { collection, authUid },
            query,
          },
        } = request;
        return this.store$.pipe(
          map(source => {
            if (!(collection in source)) {
              throw new DatastoreMissingModuleError(collection);
            }
            return source;
          }),
          select(collection, authUid),
          filter(isDefined),
          map(data => {
            const queryString = stringifyQuery(query);
            if (data.lists[queryString]) {
              return data.lists[queryString].ids.map(
                id => data.entities[id].entity,
              );
            }
            return undefined;
          }),
          filter(isDefined),
          tap(() =>
            this.requestStatusHandler.update(request, {
              ready: true,
            }),
          ),
          distinctUntilChanged(arrayIsShallowEqual),
        );
      }),
      shareReplay(1),
    );

    const statusStream$ = requestStream$.pipe(
      switchMap(request => this.requestStatusHandler.get$(request)),
      combineLatest(sourceStream$.pipe(startWith<any>(undefined))),
      map(([status]) => status),
      distinctUntilChanged(),
      shareReplay(1),
    );

    return new ListObservable(
      requestStream$.pipe(map(({ ref }) => ref)),
      this.storeBackend,
      statusStream$,
      statusStream$.pipe(
        startWith<any>(undefined),
        combineLatest(sourceStream$),
        map(([, source]) => source),
        distinctUntilChanged(),
      ),
    );
  }

  /**
   * Returns a single object from a collection specified by id.
   * If `objectId` is an observable, new objects may be fetched from the
   * network and emitted as they arrive. Note that if it emits too quickly,
   * such that the network request has not completed before the id changes,
   * that object with the previous id will not be emitted.
   *
   * @param key1 Collection name/key
   * @param key2$ Auth user id (currently logged-in user)
   * @param objectId$
   */
  object<K extends keyof Collections>(
    key1: K,
    key2$: string | Rx.Observable<string>,
    objectId$: string | number | Rx.Observable<string> | Rx.Observable<number>,
  ): ObjectObservable<K> {
    const requestStream$ = Rx.combineLatest(
      [
        toObservable(key1),
        toObservable(key2$),
        toObservable<number | string>(objectId$).pipe(distinctUntilChanged()),
      ],
      (collection: K, authUid: string, id: string | number) => {
        const path: { collection: K; authUid: string; ids: [string] } = {
          collection,
          authUid,
          ids: [id.toString()],
        };
        return { path };
      },
    ).pipe(
      switchMap(
        ({ path: { collection, authUid } }) =>
          this.store$.pipe(
            tap(source => {
              if (!(collection in source)) {
                throw new DatastoreMissingModuleError(collection);
              }
            }),
            select(collection, authUid),
            take(1),
          ),
        ({ path }, data) => ({ ref: { path }, data }),
      ),
      map(({ ref, data }) => {
        const action = {
          type: 'REQUEST_DATA',
          payload: {
            type: ref.path.collection,
            ref,
            clientRequestIds: [this.generateClientRequestId()],
          },
        };
        this.store$.dispatch(action);
        return action.payload;
      }),
      tap(request =>
        this.requestStatusHandler.update(request, {
          ready: false,
        }),
      ),
      shareReplay(1),
    );

    // TODO: Rx.merge causes double emission - T53648
    const sourceStream$ = (key1 === 'onlineOffline' // include websocket subscription stream
      ? Rx.merge(
          this.webSocketService
            .subscribeOnlineOffline(
              requestStream$.pipe(
                map(
                  ({
                    ref: {
                      path: { collection, authUid, ids },
                    },
                  }) => ids,
                ),
              ),
            )
            .pipe(combineLatest(requestStream$, (ss, req) => req)),
          requestStream$,
        )
      : requestStream$
    ).pipe(
      tap(request =>
        this.requestStatusHandler.update(request, {
          ready: false,
        }),
      ),
      switchMap(request => {
        const {
          ref: {
            path: { collection, authUid, ids },
          },
        } = request;
        return this.store$.pipe(
          select(collection, authUid),
          filter(isDefined),
          map(obj => obj.entities[ids[0]] && obj.entities[ids[0]].entity),
          filter(isDefined),
          tap(() =>
            this.requestStatusHandler.update(request, {
              ready: true,
            }),
          ),
          distinctUntilChanged(),
        );
      }),
      shareReplay(1),
    );

    const statusStream$ = requestStream$.pipe(
      switchMap(request => this.requestStatusHandler.get$(request)),
      combineLatest(sourceStream$.pipe(startWith<any>(undefined))),
      map(([status]) => status),
      distinctUntilChanged(),
      shareReplay(1),
    );

    return new ObjectObservable(
      requestStream$.pipe(map(({ ref }) => ref)),
      this.storeBackend,
      statusStream$,
      statusStream$.pipe(
        startWith<any>(undefined),
        combineLatest(sourceStream$),
        map(([, source]) => source),
        distinctUntilChanged(),
      ),
    );
  }

  /**
   * Returns a list of objects from a collection specified by id.
   * If an observable is passed in for `objectIds` then only the delta will be
   * refetched from the network.
   *
   * @param key1 Collection name/key
   * @param key2$ Auth user id (currently logged-in user)
   * @param objectIds$
   */
  objects<K extends keyof Collections>(
    key1: K,
    key2$: string | Rx.Observable<string>,
    objectIds$:
      | ReadonlyArray<number>
      | ReadonlyArray<string>
      | Rx.Observable<ReadonlyArray<number>>
      | Rx.Observable<ReadonlyArray<string>>,
  ): Rx.Observable<ReadonlyArray<Collections[K]>> {
    const requestStream$ = Rx.combineLatest(
      [
        toObservable(key1),
        toObservable(key2$),
        // FIXME: this should be ReadonlyArray<number> | ReadonlyArray<string>
        // https://github.com/Microsoft/TypeScript/issues/7294
        toObservable<ReadonlyArray<number | string>>(objectIds$).pipe(
          distinctUntilChanged(arrayIsShallowEqual),
        ),
      ],
      (collection: K, authUid: string, ids: ReadonlyArray<number | string>) => {
        const path: { collection: K; authUid: string; ids: string[] } = {
          collection,
          authUid,
          ids: ids.map(id => id.toString()),
        };
        return { path };
      },
    ).pipe(
      // Add an path at the start with an empty list of ids
      duplicateFirst<{
        path: { collection: K; authUid: string; ids: string[] };
      }>(({ path: { collection, authUid } }) => ({
        path: { collection, authUid, ids: [] },
      })),
      pairwise(), // Use pairwise to calculate and fetch new objects only
      switchMap(
        ([
          {
            path: { collection, authUid },
          },
        ]) => this.store$.pipe(select(collection, authUid), take(1)),
        (paths, data) => ({ paths, data }),
      ),
      map(
        ({
          paths: [
            {
              path: { ids: idsOrig },
            },
            {
              path: { collection, authUid, ids: idsNew },
            },
          ],
          data,
        }) => {
          const newIds = setDiff(idsNew, idsOrig).filter(
            id =>
              data && data.entities[id]
                ? toNumber(data.entities[id].timeFetched) +
                    this.REFETCH_TIMEOUT <
                  Date.now()
                : true,
          );
          const action = {
            type: 'REQUEST_DATA',
            payload: {
              type: collection,
              ref: { path: { collection, authUid, ids: newIds } },
              clientRequestIds: [this.generateClientRequestId()],
            },
          };
          if (newIds.length) {
            this.store$.dispatch(action);
          }
          return {
            ...action.payload,
            ...{ ref: { path: { collection, authUid, ids: idsNew } } },
          };
        },
      ),
      shareReplay(1),
    );

    // FIXME: This will double emit for `key1 === 'onlineOffline'` due to Rx.merge
    const sourceStream$ =
      key1 === 'onlineOffline' // include websocket subscription stream
        ? Rx.merge(
            this.webSocketService
              .subscribeOnlineOffline(
                requestStream$.pipe(
                  map(
                    ({
                      ref: {
                        path: { collection, authUid, ids },
                      },
                    }) => ids,
                  ),
                ),
              )
              .pipe(combineLatest(requestStream$, (ss, ref) => ref)),
            requestStream$,
          )
        : requestStream$;

    return sourceStream$.pipe(
      switchMap(({ ref: { path: { collection, authUid, ids } } }) =>
        this.store$.pipe(
          map(source => {
            if (!(collection in source)) {
              throw new DatastoreMissingModuleError(collection);
            }
            return source;
          }),
          select(collection, authUid),
          filter(isDefined),
          map(entitiesObject =>
            objectFilterByKeys<StoreState[K][string]['entities'][string]>(
              entitiesObject.entities,
              ids,
            ),
          ),
          map(
            entitiesObject =>
              Object.keys(entitiesObject).length
                ? Object.values(entitiesObject)
                : undefined,
          ),
          filter(isDefined),
          map(entities => entities.map(entity => entity.entity)),
          distinctUntilChanged(sameElements),
        ),
      ),
      shareReplay(1),
    );
  }

  // Hits an elastic search endpoint directly and returns the result.
  // The data doesn't get stored in the datastore (at least yet).
  search<K extends keyof SearchCollections>(
    key: K,
    query$: SearchQuery | Rx.Observable<SearchQuery>,
  ): Rx.Observable<SearchCollections[K]> {
    return this.storeBackend
      .search(key, toObservable(query$))
      .pipe(
        map(result => this.searchManager.transform(key, result)),
        shareReplay(1),
      );
  }

  /* Creates a single object.
  */
  createObject<K extends keyof Collections>(
    key1: K,
    key2$: string | Rx.Observable<string>,
    object: Collections[K],
    extra?: { readonly [index: string]: string | number },
  ): Promise<BackendPushResponse<K>> {
    return Rx.combineLatest(
      [toObservable(key1), toObservable(key2$)],
      (path1: K, path2: string) => {
        const path: Path<K> = {
          collection: path1,
          authUid: path2,
        };
        return { path };
      },
    )
      .pipe(
        take(1),
        switchMap((ref: Reference<K>) =>
          this.storeBackend.push(ref, object, extra),
        ),
      )
      .toPromise();
  }

  private generateClientRequestId() {
    return Math.random()
      .toString(36)
      .substr(2, 16);
  }
}
